import React, { useMemo, useState } from 'react';
import { FaPlusCircle, FaSearch } from 'react-icons/fa';
import { Button as RBButton, Col, Form, Row } from 'react-bootstrap';
import { useBoolean } from 'usehooks-ts';
import Select from 'react-select';
import { MenuMasterFormModal, MenuMasterTable } from './components';
import { CompanyMaster } from "../../models/companymaster/CompanyMaster";
import axios, { AxiosError } from 'axios';
import { useQueryClient } from '@tanstack/react-query';
import { useCompanyMaster } from "../../queries";
import { useAddMenuMaster, useDeleteMenuMaster, useMenuMaster, useUpdateMenuMaster } from "./queries";
import { MenuMaster } from "../../models/config/MenuMaster";
import Swal from "sweetalert2";
import { extractAccessToken } from "../../helpers/jwt";

const EXCLUDED_SIDS = new Set(["1IbuASmpqIapjmhVfzcLF4XPVY9", "1JeqtQlfd6fuDpsFtUjp01mOR98"]);

const selectSmStyles = {
    control: (base) => ({ ...base, minHeight: '31px', height: '31px' }),
    valueContainer: (base) => ({ ...base, height: '31px', padding: '0 6px' }),
    indicatorsContainer: (base) => ({ ...base, height: '31px' }),
    dropdownIndicator: (base) => ({ ...base, padding: '4px' }),
    clearIndicator: (base) => ({ ...base, padding: '4px' }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

const getErrorMessage = (error: unknown) =>
    axios.isAxiosError(error)
        ? (error as AxiosError<{ message?: string }>).response?.data.message
        : error instanceof Error
            ? error.message
            : 'Unknown error occurred.';

export const LegacyMenuMasterConfig = () => {
    const queryClient = useQueryClient();

    const { data: companyMaster } = useCompanyMaster();
    const companyOptions = useMemo(() => {
        if (!companyMaster) {
            return undefined;
        }

        const options: CompanyMaster[] = [
            { sid: 'all', company_name: 'เลือกทั้งหมด' } as CompanyMaster
        ];

        return options.concat(
            companyMaster
                .filter(c => !EXCLUDED_SIDS.has(c.sid))
                .slice()
                .sort((a, b) => (a.company_code || '').localeCompare(b.company_code || ''))
        );
    }, [companyMaster]);

    const [pendingCompany, setPendingCompany] = useState<CompanyMaster>();
    const [selectedCompany, setSelectedCompany] = useState<CompanyMaster>();

    const handleSearch = () => {
        setSelectedCompany(pendingCompany);
    };

    const { data: menuMaster, isFetching: isFetchingMenuMaster } = useMenuMaster();
    const filteredMenuMaster = useMemo(
        () =>
            menuMaster
                ?.filter(menu => {
                    if (!selectedCompany || selectedCompany.sid === 'all') {
                        return true;
                    }
                    return menu.sid === selectedCompany.sid;
                })
                .slice()
                .sort((a, b) => (a.id || '').localeCompare(b.id || '')),
        [menuMaster, selectedCompany]
    );

    const { value: isOpenCreateModal, toggle: toggleIsOpenCreateModal } = useBoolean();
    const { mutate: addMenuMaster } = useAddMenuMaster();

    const [selectedEditMenuMasters, setSelectedEditMenuMasters] = useState<MenuMaster[]>();
    const isOpenEditModal = !!selectedEditMenuMasters;
    const { mutate: updateMenuMaster } = useUpdateMenuMaster();

    const { mutate: deleteMenuMaster } = useDeleteMenuMaster();

    const handleSubmitAddMenuMaster = (menuMasters: MenuMaster[]) => {
        const requests = menuMasters.map(menu => ({
            ...menu,
            created_by: extractAccessToken().citizen_id || extractAccessToken().username
        }));

        requests.forEach(request =>
            addMenuMaster(request, {
                onSuccess: async () => {
                    await Swal.fire({ title: 'Success!', icon: 'success' });
                },
                onError: async error => {
                    await Swal.fire({ title: 'Error!', icon: 'error', text: getErrorMessage(error) });
                },
                onSettled: async () => {
                    toggleIsOpenCreateModal();
                    await queryClient.invalidateQueries({ queryKey: ['menu-master'] });
                }
            })
        );
    };

    const handleEditMenuMasters = (menuMasters: MenuMaster[]) => {
        setSelectedEditMenuMasters(menuMasters);
    };

    const handleCloseEditModal = () => {
        setSelectedEditMenuMasters(undefined);
    };

    const handleSubmitEditMenuMaster = async (menuMasters: MenuMaster[]) => {
        if (menuMasters.length === 0 || !filteredMenuMaster) {
            return;
        }

        const menuMasterSIDSet = new Set(menuMasters.map(menu => menu.sid));
        const currentMenus = filteredMenuMaster.filter(menu => menu.id === menuMasters[0].id);
        const currentMenuSIDSet = new Set(currentMenus.map(menu => menu.sid));

        const deleteMenuRequests = currentMenus
            .filter(existingMenu => !menuMasterSIDSet.has(existingMenu.sid))
            .map(menu => ({ sid: menu.sid, id: menu.id }));
        const deleteMenuSIDSet = new Set(deleteMenuRequests.map(menu => menu.sid));

        const addMenuRequests = menuMasters
            .filter(menu => !currentMenuSIDSet.has(menu.sid))
            .map(menu => ({
                ...menu,
                created_by: extractAccessToken().citizen_id || extractAccessToken().username
            }));
        const addMenuSIDSet = new Set(addMenuRequests.map(menu => menu.sid));

        const editMenuRequests = menuMasters
            .filter(menu => !deleteMenuSIDSet.has(menu.sid) && !addMenuSIDSet.has(menu.sid))
            .map(menu => ({
                ...menu,
                updated_by: extractAccessToken().citizen_id || extractAccessToken().username
            }));

        try {
            await Promise.all([
                ...deleteMenuRequests.map(request => deleteMenuMaster(request)),
                ...addMenuRequests.map(request => addMenuMaster(request)),
                ...editMenuRequests.map(request => updateMenuMaster(request))
            ]);

            handleCloseEditModal();
            await Swal.fire({ title: 'Success!', icon: 'success' });
            await queryClient.invalidateQueries({ queryKey: ['menu-master'] });
        } catch (error) {
            await Swal.fire({ title: 'Error!', icon: 'error', text: getErrorMessage(error) });
        }
    };

    const handleDeleteMenuMaster = async (menuMasters: MenuMaster[]) => {
        const result = await Swal.fire({
            title: 'ลบข้อมูล ' + menuMasters[0]?.id + ' ?',
            text: 'คุณต้องการลบหรือไม่ ?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: '✔ ใช่',
            cancelButtonText: '✖ ไม่',
            allowOutsideClick: false
        });
        if (!result.isConfirmed) {
            return;
        }

        const requests = menuMasters.map(menuMaster => ({
            sid: menuMaster.sid,
            id: menuMaster.id
        }));

        requests.forEach(request =>
            deleteMenuMaster(request, {
                onSuccess: async () => {
                    await Swal.fire({ title: 'Success!', icon: 'success' });
                },
                onError: async error => {
                    await Swal.fire({ title: 'Error!', icon: 'error', text: getErrorMessage(error) });
                },
                onSettled: async () => {
                    await queryClient.invalidateQueries({ queryKey: ['menu-master'] });
                }
            })
        );
    };

    return (
        <div className="master-data-content">
            <div>
                <div className="trapezoid-head" style={{ width: 'auto', display: 'inline-block' }}>
                    <span>Workflow Menu Master Config</span>
                </div>
                <div style={{ display: 'inline', float: 'right' }}>
                    <RBButton variant="info" size="sm" style={{ float: 'right', marginLeft: '5px' }} onClick={toggleIsOpenCreateModal}>
                        <FaPlusCircle style={{ marginBottom: '4px' }} /> Add
                    </RBButton>
                </div>
            </div>

            <div className="mat-box shadow p-3 mb-5 bg-white rounded">
                <div className='trapezoid trapezoid-purple' style={{ width: '200px', display: 'inline-block' }}>
                    <span>Search menu</span>
                </div>
                <div className="mat-box shadow p-3 mb-5 bg-white rounded">
                    <Row>
                        <Col sm={4}>
                            <Form.Group className="mb-3">
                                <Form.Label>Company :</Form.Label>
                                <Select
                                    menuPortalTarget={document.body}
                                    styles={selectSmStyles}
                                    options={companyOptions}
                                    value={pendingCompany}
                                    getOptionLabel={(company: CompanyMaster) => company.company_name}
                                    getOptionValue={(company: CompanyMaster) => company.sid}
                                    onChange={setPendingCompany}
                                    isClearable
                                />
                            </Form.Group>
                        </Col>
                        <Col lg={{ span: 2, offset: 6 }}>
                            <div className="mb-3 w-100 mt-4 d-flex justify-content-end">
                                <RBButton variant="info" size="sm" className="mx-1 btn btn-block" onClick={handleSearch}>
                                    <FaSearch /> Search
                                </RBButton>
                            </div>
                        </Col>
                    </Row>
                </div>

                <MenuMasterTable
                    isPending={isFetchingMenuMaster}
                    menus={filteredMenuMaster}
                    onEdit={handleEditMenuMasters}
                    onDelete={handleDeleteMenuMaster}
                />

                <MenuMasterFormModal
                    title="Create Menu ID"
                    isOpen={isOpenCreateModal}
                    onClose={toggleIsOpenCreateModal}
                    onSubmit={handleSubmitAddMenuMaster}
                />

                <MenuMasterFormModal
                    disabledKeyField
                    title="Edit Menu ID"
                    defaultValues={selectedEditMenuMasters}
                    isOpen={isOpenEditModal}
                    onClose={handleCloseEditModal}
                    onSubmit={handleSubmitEditMenuMaster}
                />
            </div>
        </div>
    );
}
